import { Search, Loader2, ExternalLink } from "lucide-react"
import { TOOL_LABELS } from "@/lib/ai-types"
import type { RichToolCall } from "@/lib/ag-ui-types"
import { Link } from "react-router-dom"

function parseResults(result: string | null): Array<{ name?: string; title?: string; url?: string; id?: string; description?: string }> {
  if (!result) return []
  try {
    const parsed = JSON.parse(result)
    if (Array.isArray(parsed)) return parsed
    if (parsed.results) return parsed.results
    if (parsed.data) return Array.isArray(parsed.data) ? parsed.data : [parsed.data]
    return []
  } catch {
    return []
  }
}

function isExternalUrl(url: string): boolean {
  return url.startsWith("http://") || url.startsWith("https://")
}

function parseQuery(args: string): string {
  try {
    const parsed = JSON.parse(args)
    return parsed.query || parsed.q || parsed.term || ""
  } catch {
    return ""
  }
}

export function SearchResultsCard({ toolCall }: { toolCall: RichToolCall }) {
  const label = TOOL_LABELS[toolCall.name] || toolCall.name
  const query = parseQuery(toolCall.args)
  const results = parseResults(toolCall.result)
  const isDone = toolCall.status === "done"

  return (
    <div
      className="my-2 overflow-hidden rounded-lg"
      style={{ border: "1px solid var(--border)", background: "var(--bg-surface)" }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2"
        style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-surface-raised)" }}
      >
        {isDone ? (
          <Search size={13} style={{ color: "var(--primary)" }} />
        ) : (
          <Loader2 size={13} className="animate-spin" style={{ color: "var(--primary)" }} />
        )}
        <span className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>{label}</span>
        {query && (
          <span className="ml-auto text-[11px]" style={{ color: "var(--text-muted)" }}>"{query}"</span>
        )}
      </div>

      {/* Results */}
      {isDone && results.length > 0 && (
        <div>
          {results.slice(0, 5).map((item, i) => {
            const title = item.name || item.title || `Wynik ${i + 1}`
            const url = item.url
            const external = url && isExternalUrl(url)

            return (
              <div
                key={item.id || i}
                className="px-3 py-1.5"
                style={{ borderTop: i > 0 ? "1px solid var(--border)" : undefined }}
              >
                <div className="flex items-center gap-2">
                  <span className="flex-1 truncate text-xs font-medium" style={{ color: "var(--text-primary)" }}>{title}</span>
                  {url && external ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary)" }} className="shrink-0 hover:opacity-80">
                      <ExternalLink size={11} />
                    </a>
                  ) : url ? (
                    <Link to={url} style={{ color: "var(--primary)" }} className="shrink-0 hover:opacity-80">
                      <ExternalLink size={11} />
                    </Link>
                  ) : null}
                </div>
                {item.description && (
                  <p className="mt-0.5 line-clamp-2 text-[11px] leading-tight" style={{ color: "var(--text-muted)" }}>{item.description}</p>
                )}
              </div>
            )
          })}
          {results.length > 5 && (
            <div
              className="px-3 py-1.5 text-[11px]"
              style={{ color: "var(--text-muted)", borderTop: "1px solid var(--border)" }}
            >
              ...i {results.length - 5} więcej
            </div>
          )}
        </div>
      )}

      {isDone && results.length === 0 && (
        <div className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>Brak wyników</div>
      )}

      {!isDone && (
        <div className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>Szukam...</div>
      )}
    </div>
  )
}
